import { useState, useEffect, useCallback } from "react";

// ─── STYLES ────────────────────────────────────────────
const styles = `
@import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=DM+Sans:wght@300;400;500;600&display=swap');

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

:root {
  --green: #22c55e; --green-light: #4ade80; --green-dim: rgba(34,197,94,0.1);
  --green-border: rgba(34,197,94,0.25); --green-glow: rgba(34,197,94,0.15);
  --bg: #080c10; --bg2: #0d1117; --bg3: #111820;
  --surface: rgba(255,255,255,0.04); --border: rgba(255,255,255,0.08);
  --text: #f0f4f8; --text2: #8b9cb0; --text3: #556070;
  --font-display: 'Syne', sans-serif; --font-body: 'DM Sans', sans-serif;
  --radius: 16px; --radius-sm: 10px;
}

body { font-family: var(--font-body); background: var(--bg); color: var(--text); min-height: 100vh; overflow-x: hidden; }

.onboard-wrap {
  min-height: 100vh; display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  padding: 40px 20px; position: relative; overflow: hidden;
}

/* Background */
.onboard-wrap::before {
  content: ''; position: fixed; inset: 0; z-index: 0;
  background:
    radial-gradient(ellipse 70% 60% at 50% 0%, rgba(34,197,94,0.08) 0%, transparent 60%),
    radial-gradient(ellipse 40% 40% at 80% 80%, rgba(34,197,94,0.04) 0%, transparent 60%);
}
.grid-bg {
  position: fixed; inset: 0; z-index: 0;
  background-image: linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px);
  background-size: 60px 60px;
  mask-image: radial-gradient(ellipse 80% 80% at 50% 50%, black 30%, transparent 100%);
}

.onboard-card {
  position: relative; z-index: 1;
  width: 100%; max-width: 560px;
  background: var(--bg2); border: 1px solid var(--border);
  border-radius: 24px; overflow: hidden;
  box-shadow: 0 40px 80px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.04);
  animation: cardIn 0.6s cubic-bezier(0.16, 1, 0.3, 1) both;
}
@keyframes cardIn {
  from { opacity: 0; transform: translateY(30px) scale(0.98); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}

/* Top accent bar */
.card-accent { height: 3px; background: linear-gradient(90deg, transparent, var(--green), transparent); }

.card-body { padding: 48px 48px 40px; }

/* Logo */
.brand { display: flex; align-items: center; gap: 10px; margin-bottom: 36px; }
.brand-logo {
  font-family: var(--font-display); font-size: 20px; font-weight: 800;
  letter-spacing: -0.5px;
}
.brand-logo span { color: var(--green); }
.brand-badge {
  display: inline-flex; align-items: center; gap: 6px;
  background: var(--green-dim); border: 1px solid var(--green-border);
  color: var(--green-light); font-size: 11px; font-weight: 600;
  padding: 3px 10px; border-radius: 100px; letter-spacing: 0.5px;
}
.brand-badge::before { content: '✦'; font-size: 8px; }

/* Heading */
.card-heading { font-family: var(--font-display); font-size: 28px; font-weight: 800; letter-spacing: -1px; line-height: 1.2; margin-bottom: 10px; }
.card-sub { font-size: 15px; color: var(--text2); line-height: 1.6; margin-bottom: 36px; }

/* Steps indicator */
.steps-bar { display: flex; align-items: center; gap: 0; margin-bottom: 36px; }
.step-dot {
  display: flex; flex-direction: column; align-items: center; gap: 6px;
  position: relative;
}
.step-dot-circle {
  width: 32px; height: 32px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 12px; font-weight: 700; transition: all 0.3s;
  border: 2px solid var(--border); background: var(--surface); color: var(--text3);
}
.step-dot.active .step-dot-circle {
  border-color: var(--green); background: var(--green-dim); color: var(--green);
  box-shadow: 0 0 16px var(--green-glow);
}
.step-dot.done .step-dot-circle {
  border-color: var(--green); background: var(--green); color: #000;
}
.step-dot-label { font-size: 11px; color: var(--text3); font-weight: 500; white-space: nowrap; }
.step-dot.active .step-dot-label { color: var(--green); }
.step-dot.done .step-dot-label { color: var(--text2); }
.step-line { flex: 1; height: 2px; background: var(--border); margin: 0 8px; margin-bottom: 20px; transition: background 0.3s; }
.step-line.done { background: var(--green); }

/* Form */
.form-section { display: flex; flex-direction: column; gap: 16px; animation: fadeUp 0.4s ease both; }
@keyframes fadeUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }

.form-group { display: flex; flex-direction: column; gap: 7px; }
.form-label { font-size: 13px; font-weight: 600; color: var(--text2); letter-spacing: 0.3px; }
.form-input, .form-select {
  background: var(--surface); border: 1px solid var(--border);
  border-radius: var(--radius-sm); padding: 13px 16px;
  color: var(--text); font-family: var(--font-body); font-size: 15px;
  outline: none; transition: all 0.2s; resize: none;
  -webkit-appearance: none;
}
.form-input:focus, .form-select:focus {
  border-color: var(--green-border); box-shadow: 0 0 0 3px var(--green-dim);
}
.form-input::placeholder { color: var(--text3); }
.form-select option { background: var(--bg3); }

.form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }

/* Connect Button */
.connect-btn {
  width: 100%; padding: 16px 24px; margin-top: 8px;
  background: var(--green); color: #000;
  font-family: var(--font-body); font-size: 15px; font-weight: 700;
  border: none; border-radius: var(--radius-sm); cursor: pointer;
  display: flex; align-items: center; justify-content: center; gap: 10px;
  transition: all 0.25s; box-shadow: 0 0 30px rgba(34,197,94,0.25);
  letter-spacing: 0.2px; position: relative; overflow: hidden;
}
.connect-btn::after {
  content: ''; position: absolute; inset: 0;
  background: linear-gradient(135deg, rgba(255,255,255,0.1) 0%, transparent 50%);
  opacity: 0; transition: opacity 0.2s;
}
.connect-btn:hover { background: var(--green-light); transform: translateY(-2px); box-shadow: 0 0 40px rgba(34,197,94,0.35); }
.connect-btn:hover::after { opacity: 1; }
.connect-btn:disabled { opacity: 0.6; cursor: not-allowed; transform: none; box-shadow: none; }
.connect-btn.loading { pointer-events: none; }

.wa-icon { font-size: 18px; }

/* Secondary button */
.back-btn {
  width: 100%; padding: 13px; margin-top: 8px;
  background: transparent; color: var(--text2);
  font-family: var(--font-body); font-size: 14px; font-weight: 500;
  border: 1px solid var(--border); border-radius: var(--radius-sm);
  cursor: pointer; transition: all 0.2s;
}
.back-btn:hover { border-color: var(--green-border); color: var(--text); }

/* WhatsApp Connect section */
.wa-connect-box {
  background: var(--surface); border: 1px solid var(--border);
  border-radius: var(--radius); padding: 28px; margin-bottom: 8px;
  text-align: center;
}
.wa-connect-icon { font-size: 48px; margin-bottom: 12px; }
.wa-connect-title { font-family: var(--font-display); font-size: 18px; font-weight: 700; margin-bottom: 8px; }
.wa-connect-desc { font-size: 14px; color: var(--text2); line-height: 1.6; }

/* What happens list */
.happens-list { display: flex; flex-direction: column; gap: 10px; margin-bottom: 24px; }
.happens-item {
  display: flex; align-items: flex-start; gap: 12px;
  background: var(--surface); border: 1px solid var(--border);
  border-radius: var(--radius-sm); padding: 14px 16px;
}
.happens-icon { font-size: 16px; flex-shrink: 0; margin-top: 1px; }
.happens-text { font-size: 13px; color: var(--text2); line-height: 1.5; }
.happens-text strong { color: var(--text); font-weight: 600; }

/* Success state */
.success-box { text-align: center; animation: fadeUp 0.5s ease both; }
.success-icon-wrap {
  width: 80px; height: 80px; border-radius: 50%; margin: 0 auto 24px;
  background: var(--green-dim); border: 2px solid var(--green-border);
  display: flex; align-items: center; justify-content: center;
  font-size: 36px; animation: popIn 0.5s cubic-bezier(0.16, 1, 0.3, 1) both;
}
@keyframes popIn { from { transform: scale(0.5); opacity: 0; } to { transform: scale(1); opacity: 1; } }
.success-title { font-family: var(--font-display); font-size: 26px; font-weight: 800; letter-spacing: -0.5px; margin-bottom: 10px; }
.success-sub { font-size: 15px; color: var(--text2); line-height: 1.6; margin-bottom: 32px; }

/* Client details card */
.client-details {
  background: var(--surface); border: 1px solid var(--green-border);
  border-radius: var(--radius); padding: 20px; margin-bottom: 28px; text-align: left;
}
.client-details-row { display: flex; justify-content: space-between; align-items: center; padding: 8px 0; border-bottom: 1px solid var(--border); }
.client-details-row:last-child { border-bottom: none; padding-bottom: 0; }
.client-details-label { font-size: 12px; color: var(--text3); text-transform: uppercase; letter-spacing: 0.8px; }
.client-details-value { font-size: 14px; color: var(--text); font-weight: 600; font-family: monospace; }

/* Progress spinner */
.spinner {
  width: 18px; height: 18px; border: 2px solid rgba(0,0,0,0.3);
  border-top-color: #000; border-radius: 50%;
  animation: spin 0.7s linear infinite; flex-shrink: 0;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* Error */
.error-box {
  background: rgba(239,68,68,0.1); border: 1px solid rgba(239,68,68,0.25);
  border-radius: var(--radius-sm); padding: 14px 16px; margin-top: 12px;
  font-size: 13px; color: #fca5a5; display: flex; gap: 10px; align-items: flex-start;
}

/* Trust badges */
.trust-row {
  display: flex; justify-content: center; align-items: center; gap: 20px;
  padding-top: 24px; border-top: 1px solid var(--border);
  flex-wrap: wrap;
}
.trust-badge { display: flex; align-items: center; gap: 6px; font-size: 12px; color: var(--text3); }
.trust-badge svg { width: 14px; height: 14px; color: var(--green); flex-shrink: 0; }

/* Meta branding note */
.meta-note {
  text-align: center; margin-top: 16px;
  font-size: 12px; color: var(--text3);
}
.meta-note a { color: var(--text2); text-decoration: none; }
.meta-note a:hover { color: var(--green); }

@media (max-width: 600px) {
  .card-body { padding: 32px 24px 28px; }
  .form-row { grid-template-columns: 1fr; }
  .card-heading { font-size: 24px; }
}
`;

// ─── CONSTANTS ─────────────────────────────────────────
const APP_ID = "1862656744391329";
const BACKEND_URL = "https://whatsapp-hook-production.up.railway.app";

const BUSINESS_TYPES = [
    "Wellness Centre", "Yoga Studio", "Spa & Salon",
    "Dental Clinic", "Coaching / Consulting",
    "Gym / Fitness Centre", "Medical Clinic", "Other"
];

// ─── STEP INDICATOR ────────────────────────────────────
function StepsBar({ current }) {
    const steps = ["Business Info", "Connect WhatsApp", "Go Live"];
    return (
        <div className="steps-bar">
            {steps.map((label, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", flex: i < steps.length - 1 ? "1" : "none" }}>
                    <div className={`step-dot ${current === i ? "active" : ""} ${current > i ? "done" : ""}`}>
                        <div className="step-dot-circle">
                            {current > i ? "✓" : i + 1}
                        </div>
                        <span className="step-dot-label">{label}</span>
                    </div>
                    {i < steps.length - 1 && <div className={`step-line ${current > i ? "done" : ""}`} />}
                </div>
            ))}
        </div>
    );
}

// ─── STEP 1: BUSINESS INFO ─────────────────────────────
function StepBusinessInfo({ onNext }) {
    const [form, setForm] = useState({
        businessName: "", ownerName: "", phone: "",
        email: "", city: "", type: ""
    });
    const [errors, setErrors] = useState({});

    const validate = () => {
        const e = {};
        if (!form.businessName.trim()) e.businessName = "Required";
        if (!form.ownerName.trim()) e.ownerName = "Required";
        if (!form.phone.trim() || form.phone.length < 10) e.phone = "Enter valid phone";
        if (!form.email.includes("@")) e.email = "Enter valid email";
        if (!form.type) e.type = "Required";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handle = e => {
        setForm(f => ({ ...f, [e.target.name]: e.target.value }));
        setErrors(err => ({ ...err, [e.target.name]: "" }));
    };

    return (
        <div className="form-section">
            <div className="form-row">
                <div className="form-group">
                    <label className="form-label">Business Name *</label>
                    <input className="form-input" name="businessName" value={form.businessName}
                        onChange={handle} placeholder="Serenity Wellness" />
                    {errors.businessName && <span style={{ color: "#fca5a5", fontSize: 12 }}>{errors.businessName}</span>}
                </div>
                <div className="form-group">
                    <label className="form-label">Owner Name *</label>
                    <input className="form-input" name="ownerName" value={form.ownerName}
                        onChange={handle} placeholder="Priya Sharma" />
                    {errors.ownerName && <span style={{ color: "#fca5a5", fontSize: 12 }}>{errors.ownerName}</span>}
                </div>
            </div>

            <div className="form-row">
                <div className="form-group">
                    <label className="form-label">WhatsApp Number *</label>
                    <input className="form-input" name="phone" value={form.phone}
                        onChange={handle} placeholder="9876543210" maxLength={10} />
                    {errors.phone && <span style={{ color: "#fca5a5", fontSize: 12 }}>{errors.phone}</span>}
                </div>
                <div className="form-group">
                    <label className="form-label">Email Address *</label>
                    <input className="form-input" name="email" type="email" value={form.email}
                        onChange={handle} placeholder="you@yourbusiness.com" />
                    {errors.email && <span style={{ color: "#fca5a5", fontSize: 12 }}>{errors.email}</span>}
                </div>
            </div>

            <div className="form-row">
                <div className="form-group">
                    <label className="form-label">Business Type *</label>
                    <select className="form-select" name="type" value={form.type} onChange={handle}>
                        <option value="">Select type</option>
                        {BUSINESS_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                    {errors.type && <span style={{ color: "#fca5a5", fontSize: 12 }}>{errors.type}</span>}
                </div>
                <div className="form-group">
                    <label className="form-label">City</label>
                    <input className="form-input" name="city" value={form.city}
                        onChange={handle} placeholder="Jaipur" />
                </div>
            </div>

            <button className="connect-btn" onClick={() => validate() && onNext(form)}>
                Continue → Connect WhatsApp
            </button>
        </div>
    );
}

// ─── STEP 2: WHATSAPP CONNECT ──────────────────────────
function StepConnectWhatsApp({ businessInfo, onSuccess, onBack }) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    // Launch Facebook Embedded Signup popup
    const launchEmbeddedSignup = useCallback(() => {
        setLoading(true);
        setError("");

        if (!window.FB) {
            setError("Facebook SDK not loaded. Please refresh and try again.");
            setLoading(false);
            return;
        }

        const handleSignupResponse = async (response) => {
            if (response.authResponse) {
                const code = response.authResponse.code;
                try {
                    // Exchange code for token via your backend
                    const res = await fetch(`${BACKEND_URL}/api/embedded-signup`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ code, businessInfo })
                    });

                    const data = await res.json();

                    if (data.success) {
                        onSuccess(data.client);
                    } else {
                        setError(data.error || "Failed to connect WhatsApp. Please try again.");
                        setLoading(false);
                    }
                } catch (err) {
                    setError("Connection failed. Please try again.");
                    setLoading(false);
                }
            } else {
                setError("WhatsApp connection was cancelled. Please try again.");
                setLoading(false);
            }
        };

        window.FB.login(
            (response) => {
                handleSignupResponse(response);
            },
            {
                config_id: "803915259008129",
                response_type: "code",
                override_default_response_type: true,
                extras: {
                    setup: {
                        // Pre-fill business details
                        business: {
                            name: businessInfo.businessName,
                            email: businessInfo.email,
                            phone: {
                                code: "91",
                                number: businessInfo.phone
                            },
                            website: "https://bookwello.in",
                            address: {
                                city: businessInfo.city || "India",
                                country_code: "IN"
                            }
                        }
                    },
                    featureType: "whatsapp_embedded_signup",
                    sessionInfoVersion: "3"
                }
            }
        );
    }, [businessInfo, onSuccess]);

    return (
        <div className="form-section">
            <div className="wa-connect-box">
                <div className="wa-connect-icon">📱</div>
                <div className="wa-connect-title">Connect your WhatsApp</div>
                <div className="wa-connect-desc">
                    You'll be redirected to Meta to connect your WhatsApp Business account.
                    This takes about 2 minutes.
                </div>
            </div>

            <div className="happens-list">
                {[
                    { icon: "🔐", text: <><strong>Secure login</strong> — Log in with your Facebook account that manages your WhatsApp Business</> },
                    { icon: "📞", text: <><strong>Select your number</strong> — Choose or create your WhatsApp Business number</> },
                    { icon: "✅", text: <><strong>Grant access</strong> — Allow Bookwello to manage bookings on your behalf</> },
                    { icon: "🚀", text: <><strong>Go live instantly</strong> — Your booking bot activates automatically</> },
                ].map((item, i) => (
                    <div key={i} className="happens-item">
                        <span className="happens-icon">{item.icon}</span>
                        <span className="happens-text">{item.text}</span>
                    </div>
                ))}
            </div>

            <button
                className={`connect-btn ${loading ? "loading" : ""}`}
                onClick={launchEmbeddedSignup}
                disabled={loading}
            >
                {loading ? (
                    <><div className="spinner" />Connecting...</>
                ) : (
                    <><span className="wa-icon">💬</span>Connect WhatsApp Business</>
                )}
            </button>

            {error && (
                <div className="error-box">
                    <span>⚠️</span>
                    <span>{error}</span>
                </div>
            )}

            <button className="back-btn" onClick={onBack} disabled={loading}>
                ← Back
            </button>

            <p className="meta-note">
                By connecting, you agree to Meta's{" "}
                <a href="https://www.whatsapp.com/legal/business-policy" target="_blank" rel="noreferrer">
                    WhatsApp Business Policy
                </a>{" "}
                and Bookwello's{" "}
                <a href="https://bookwello.in/terms" target="_blank" rel="noreferrer">
                    Terms of Service
                </a>
            </p>
        </div>
    );
}

// ─── STEP 3: SUCCESS ───────────────────────────────────
function StepSuccess({ client }) {
    return (
        <div className="success-box">
            <div className="success-icon-wrap">🎉</div>
            <div className="success-title">You're live on Bookwello!</div>
            <p className="success-sub">
                Your WhatsApp booking bot is now active. Customers can start booking
                appointments by messaging your WhatsApp number right now.
            </p>

            <div className="client-details">
                {[
                    { label: "Business", value: client?.name || "Your Business" },
                    { label: "WhatsApp Number", value: `+${client?.whatsapp_number || "Connected"}` },
                    { label: "Status", value: "🟢 Active" },
                    { label: "Plan", value: client?.plan || "Standard" },
                ].map(row => (
                    <div key={row.label} className="client-details-row">
                        <span className="client-details-label">{row.label}</span>
                        <span className="client-details-value">{row.value}</span>
                    </div>
                ))}
            </div>

            <button className="connect-btn" onClick={() => window.location.href = "/dashboard"}>
                Go to Dashboard →
            </button>

            <div style={{ marginTop: 16 }}>
                <button className="back-btn" onClick={() => window.location.href = "/"}>
                    Back to Home
                </button>
            </div>
        </div>
    );
}

// ─── MAIN COMPONENT ────────────────────────────────────
export default function EmbeddedSignup() {
    const [step, setStep] = useState(0);
    const [businessInfo, setBusinessInfo] = useState(null);
    const [client, setClient] = useState(null);

    // Load Facebook SDK (already in index.html but init here)
    useEffect(() => {
        if (window.FB) {
            window.FB.init({
                appId: APP_ID,
                cookie: true,
                xfbml: true,
                version: "v19.0"
            });
        }
    }, []);

    const headings = [
        { title: "Set up your account", sub: "Tell us about your business so we can configure your booking bot." },
        { title: "Connect WhatsApp", sub: `Welcome, ${businessInfo?.businessName || ""}! One click to activate your booking bot.` },
        { title: "Booking bot active!", sub: "" },
    ];

    return (
        <>
            <style dangerouslySetInnerHTML={{ __html: styles }} />
            <div className="onboard-wrap">
                <div className="grid-bg" />
                <div className="onboard-card">
                    <div className="card-accent" />
                    <div className="card-body">

                        {/* Brand */}
                        <div className="brand">
                            <div className="brand-logo">book<span>wello</span></div>
                            <span className="brand-badge">Onboarding</span>
                        </div>

                        {/* Steps */}
                        <StepsBar current={step} />

                        {/* Heading */}
                        {step < 2 && (
                            <>
                                <h1 className="card-heading">{headings[step].title}</h1>
                                <p className="card-sub">{headings[step].sub}</p>
                            </>
                        )}

                        {/* Step content */}
                        {step === 0 && (
                            <StepBusinessInfo
                                onNext={(info) => { setBusinessInfo(info); setStep(1); }}
                            />
                        )}
                        {step === 1 && (
                            <StepConnectWhatsApp
                                businessInfo={businessInfo}
                                onSuccess={(data) => { setClient(data); setStep(2); }}
                                onBack={() => setStep(0)}
                            />
                        )}
                        {step === 2 && <StepSuccess client={client} />}

                        {/* Trust badges */}
                        {step < 2 && (
                            <div className="trust-row">
                                {[
                                    { icon: "🔒", text: "End-to-end encrypted" },
                                    { icon: "✅", text: "Meta verified platform" },
                                    { icon: "🇮🇳", text: "Built in India" },
                                ].map(b => (
                                    <div key={b.text} className="trust-badge">
                                        <span>{b.icon}</span>
                                        <span>{b.text}</span>
                                    </div>
                                ))}
                            </div>
                        )}

                    </div>
                </div>
            </div>
        </>
    );
}
